import { useCallback } from 'react';
import type { OnboardingApiClient } from '@/features/onboarding/api/onboarding-api-client';
import {
  FactorType,
  FlowStatus,
  ChallengeStatus,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';
import { getFactorErrorMessage } from '@/features/onboarding/utils/factor-error-message';
import { useAaguidCatalogQuery } from '@/hooks/use-aaguid-catalog.queries';

const CODE_FACTOR_TYPES: ReadonlySet<FactorType> = new Set([
  FactorType.TOTP,
  FactorType.SMS_OTP,
  FactorType.WHATSAPP_OTP,
]);

interface UseSubmitFactorParams {
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  processResponse: (response: OnboardingFlowResponse) => Promise<void>;
  callCompleteApi: () => Promise<string | null>;
  api: OnboardingApiClient;
  shouldAutoComplete: (response: OnboardingFlowResponse) => boolean;
}

interface UseSubmitFactorReturn {
  submitFactor: (payload: Record<string, unknown>) => Promise<boolean>;
}

export function useSubmitFactor({
  setState,
  processResponse,
  callCompleteApi,
  api,
  shouldAutoComplete,
}: UseSubmitFactorParams): UseSubmitFactorReturn {
  const { data: catalog } = useAaguidCatalogQuery();

  /**
   * Generic factor submission - works for ANY factor type
   * Used by the FactorStepRenderer factory pattern
   * Backend determines what to do based on session state
   *
   * Handles RETRY_REQUIRED status by showing factor-specific error messages
   * without transitioning the flow state (user can retry the same step)
   *
   * Auto-completes when canComplete=true and no more factors to show,
   * instead of relying on useEffect which can miss due to timing issues.
   */
  const submitFactor = useCallback(
    async (payload: Record<string, unknown>) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.submitFactor(payload);

        // Handle retry-required: verification failed but user can try again
        // Backend returns IN_PROGRESS with currentStep.status = RETRY_REQUIRED
        if (
          response.status === FlowStatus.IN_PROGRESS &&
          response.currentStep?.status === ChallengeStatus.RETRY_REQUIRED
        ) {
          const factorType = response.currentStep.factor_type;
          const errorMessage = getFactorErrorMessage({
            factorType,
            challengeStatus: ChallengeStatus.RETRY_REQUIRED,
            metadata: response.currentStep.metadata,
            catalog,
          });

          setState(prev => ({
            ...prev,
            error: errorMessage,
            hasExceededRetries: false,
            isLoading: false,
          }));

          return false;
        }

        // Handle flow failed (e.g., max retries exceeded)
        if (
          response.status === FlowStatus.FAILED &&
          response.currentStep?.factor_type
        ) {
          const factorType = response.currentStep.factor_type;
          const errorMessage = getFactorErrorMessage({
            factorType,
            challengeStatus: ChallengeStatus.FAILED,
            metadata: response.currentStep.metadata,
            catalog,
          });

          // The backend ends the flow, so FailedStep replaces the dead-end code input
          const isCodeFailure = CODE_FACTOR_TYPES.has(factorType);

          setState(prev => ({
            ...prev,
            ...(isCodeFailure && { flowStatus: FlowStatus.FAILED }),
            error: errorMessage,
            hasExceededRetries: true,
            isLoading: false,
          }));

          return false;
        }

        // Success or other status - process normally
        await processResponse(response);

        // Auto-complete: if canComplete=true and no more factors to show,
        // route through callCompleteApi so the backend also tears down the
        // flow (marks the invitation COMPLETED, clears the session cookie,
        // and — for onboarding — returns the exchange token). The previous
        // approach of setting flowStatus directly left the BFF/enrollment
        // state stuck in ACCESSED for invitation flows whose success screen
        // never surfaces a "continue" button.
        if (shouldAutoComplete(response)) {
          await callCompleteApi();
        }

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Submission failed',
          isLoading: false,
        }));

        return false;
      }
    },
    [
      api,
      setState,
      processResponse,
      callCompleteApi,
      shouldAutoComplete,
      catalog,
    ]
  );

  return { submitFactor };
}
